import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Observable, Subject, catchError, debounceTime, map, of, switchMap } from 'rxjs';
import {
  FIELD_BORDER,
  FIELD_BORDER_ERROR,
  FIELD_ERROR,
  FIELD_HINT,
  FIELD_INPUT,
  FIELD_LABEL,
} from '../form/field-styles';
import { uniqueId } from '../form/ids';

export interface AutocompleteOption {
  readonly value: string;
  readonly label: string;
  /** Riga secondaria, per esempio la provincia di un Comune. */
  readonly detail?: string;
}

/** Ricerca dei suggerimenti: di solito una chiamata all'API (Comuni, qualifiche). */
export type AutocompleteSearch = (query: string) => Observable<readonly AutocompleteOption[]>;

/** Attesa dopo l'ultimo tasto prima di cercare. */
export const AUTOCOMPLETE_DEBOUNCE_MS = 200;

type SearchState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly options: readonly AutocompleteOption[] }
  | { readonly kind: 'done'; readonly options: readonly AutocompleteOption[] }
  | { readonly kind: 'failed' };

/**
 * Campo con suggerimenti secondo il modello combobox di WAI-ARIA 1.2: il fuoco resta nel campo,
 * le frecce scorrono i suggerimenti (`aria-activedescendant`), Invio sceglie, Esc chiude e poi
 * svuota. Il numero dei risultati si annuncia con `aria-live`. Con `allowFreeText` si accetta
 * anche un testo che non è fra i suggerimenti (`freeText`).
 */
@Component({
  selector: 'bs-autocomplete',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <label [for]="id" [class]="labelClass">
      {{ label() }}
      @if (required()) {
        <span class="text-stone-500 dark:text-stone-400" aria-hidden="true">*</span>
      }
    </label>
    <div class="relative">
      <input
        #control
        [id]="id"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        autocomplete="off"
        [attr.aria-expanded]="open()"
        [attr.aria-controls]="listId"
        [attr.aria-activedescendant]="activeId()"
        [attr.aria-invalid]="error() ? 'true' : null"
        [attr.aria-describedby]="describedBy()"
        [attr.placeholder]="placeholder()"
        [attr.data-testid]="testId()"
        [required]="required()"
        [disabled]="disabled()"
        [class]="inputClass()"
        [value]="text()"
        (input)="onInput($event)"
        (keydown)="onKeydown($event)"
        (blur)="onBlur()"
      />
      <ul
        [id]="listId"
        role="listbox"
        [attr.aria-label]="label()"
        [hidden]="!open()"
        class="absolute inset-x-0 top-full z-20 mt-1 flex max-h-72 flex-col overflow-y-auto rounded-2xl border border-stone-200 bg-white p-1.5 shadow-lg dark:border-stone-700 dark:bg-stone-900"
        [attr.data-testid]="testId() ? testId() + '-options' : null"
      >
        @for (option of options(); track option.value; let index = $index) {
          <!-- Modello combobox: la tastiera resta sul campo (aria-activedescendant), le opzioni
               rispondono solo al puntatore. -->
          <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
          <li
            [id]="optionId(index)"
            role="option"
            [attr.aria-selected]="index === activeIndex()"
            [class]="index === activeIndex() ? activeOptionClass : optionClass"
            (mousedown)="$event.preventDefault()"
            (click)="choose(option)"
          >
            <span class="truncate">{{ option.label }}</span>
            @if (option.detail; as detail) {
              <span class="truncate text-xs text-stone-500 dark:text-stone-400">{{ detail }}</span>
            }
          </li>
        }
      </ul>
    </div>
    <p class="sr-only" aria-live="polite" aria-atomic="true">{{ announcement() }}</p>
    @if (hint(); as text) {
      <p [id]="hintId" [class]="hintClass">{{ text }}</p>
    }
    @if (error(); as message) {
      <p
        [id]="errorId"
        [class]="errorClass"
        [attr.data-testid]="testId() ? testId() + '-error' : null"
      >
        {{ message }}
      </p>
    }
  `,
})
export class AutocompleteComponent {
  readonly label = input.required<string>();
  readonly search = input.required<AutocompleteSearch>();
  readonly placeholder = input<string | null>(null);
  readonly hint = input<string | null>(null);
  readonly error = input<string | null>(null);
  readonly required = input(false);
  readonly disabled = input(false);
  readonly testId = input<string | null>(null);
  /** Caratteri da scrivere prima di cercare. */
  readonly minChars = input(2);
  /** Accetta anche un testo che non è fra i suggerimenti. */
  readonly allowFreeText = input(false);
  /** Suggerimento scelto; `null` finché non se ne sceglie uno (o si cambia il testo). */
  readonly selected = model<AutocompleteOption | null>(null);
  /** Con `allowFreeText`: testo confermato con Invio o lasciando il campo senza scegliere. */
  readonly freeText = output<string>();

  protected readonly id = uniqueId('bs-autocomplete');
  protected readonly listId = `${this.id}-options`;
  protected readonly hintId = `${this.id}-hint`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly labelClass = FIELD_LABEL;
  protected readonly hintClass = FIELD_HINT;
  protected readonly errorClass = FIELD_ERROR;
  private readonly optionBase =
    'flex min-h-11 cursor-pointer flex-col justify-center rounded-xl px-3 py-1.5 text-sm text-ink dark:text-stone-50';
  protected readonly optionClass = `${this.optionBase} hover:bg-stone-100 dark:hover:bg-stone-800`;
  protected readonly activeOptionClass = `${this.optionBase} bg-bsite-100 dark:bg-stone-700`;

  protected readonly text = signal('');
  private readonly state = signal<SearchState>({ kind: 'idle' });
  private readonly expanded = signal(false);
  protected readonly activeIndex = signal(-1);
  private readonly control = viewChild.required<ElementRef<HTMLInputElement>>('control');
  private readonly queries = new Subject<string>();
  /** Il suggerimento è stato tolto perché si è scritto: il testo non va svuotato. */
  private clearedByTyping = false;

  protected readonly options = computed(() => {
    const state = this.state();
    return state.kind === 'done' || state.kind === 'loading' ? state.options : [];
  });
  protected readonly open = computed(() => this.expanded() && this.options().length > 0);
  protected readonly activeId = computed(() =>
    this.open() && this.activeIndex() >= 0 ? this.optionId(this.activeIndex()) : null,
  );
  protected readonly announcement = computed(() => {
    const state = this.state();
    switch (state.kind) {
      case 'loading':
        return 'Ricerca in corso…';
      case 'failed':
        return 'Suggerimenti non disponibili.';
      case 'done':
        if (state.options.length === 0) return 'Nessun suggerimento.';
        return state.options.length === 1
          ? '1 suggerimento disponibile.'
          : `${state.options.length} suggerimenti disponibili.`;
      default:
        return '';
    }
  });
  protected readonly inputClass = computed(
    () => `${FIELD_INPUT} ${this.error() ? FIELD_BORDER_ERROR : FIELD_BORDER}`,
  );
  protected readonly describedBy = computed(() => {
    const ids = [this.hint() ? this.hintId : null, this.error() ? this.errorId : null];
    return ids.filter(Boolean).join(' ') || null;
  });

  constructor() {
    const subscription = this.queries
      .pipe(
        debounceTime(AUTOCOMPLETE_DEBOUNCE_MS),
        switchMap((query) => {
          if (query.trim().length < this.minChars()) {
            return of<SearchState>({ kind: 'idle' });
          }
          // Durante la ricerca restano visibili i suggerimenti precedenti: niente sfarfallio.
          this.state.set({ kind: 'loading', options: this.options() });
          return this.search()(query.trim()).pipe(
            map((options): SearchState => ({ kind: 'done', options })),
            catchError(() => of<SearchState>({ kind: 'failed' })),
          );
        }),
      )
      .subscribe((state) => {
        this.state.set(state);
        this.activeIndex.set(-1);
      });
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());

    // Un suggerimento impostato da fuori (per esempio il valore salvato) compare nel campo.
    effect(() => {
      const selected = this.selected();
      untracked(() => {
        if (selected) {
          this.text.set(selected.label);
        } else if (!this.clearedByTyping) {
          this.text.set('');
        }
        this.clearedByTyping = false;
      });
    });
  }

  focus(): void {
    this.control().nativeElement.focus();
  }

  protected optionId(index: number): string {
    return `${this.id}-option-${index}`;
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.text.set(value);
    if (this.selected() !== null) {
      this.clearedByTyping = true;
      this.selected.set(null);
    }
    this.expanded.set(true);
    this.queries.next(value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const count = this.options().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.expanded()) {
          this.expanded.set(true);
        }
        if (count > 0) this.activeIndex.set((this.activeIndex() + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.expanded()) {
          this.expanded.set(true);
        }
        if (count > 0) this.activeIndex.set((this.activeIndex() - 1 + count) % count);
        break;
      case 'Enter': {
        const option = this.open() ? this.options()[this.activeIndex()] : undefined;
        if (option) {
          event.preventDefault();
          this.choose(option);
        } else if (this.allowFreeText() && this.text().trim()) {
          event.preventDefault();
          this.expanded.set(false);
          this.freeText.emit(this.text().trim());
        }
        break;
      }
      case 'Escape':
        if (this.open()) {
          event.preventDefault();
          this.expanded.set(false);
          this.activeIndex.set(-1);
        } else if (this.text()) {
          event.preventDefault();
          this.text.set('');
          this.selected.set(null);
          this.state.set({ kind: 'idle' });
        }
        break;
    }
  }

  protected onBlur(): void {
    this.expanded.set(false);
    this.activeIndex.set(-1);
    if (this.allowFreeText() && this.selected() === null && this.text().trim()) {
      this.freeText.emit(this.text().trim());
    }
  }

  protected choose(option: AutocompleteOption): void {
    this.selected.set(option);
    this.text.set(option.label);
    this.expanded.set(false);
    this.activeIndex.set(-1);
  }
}
